import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _keyBy from 'lodash/keyBy';
import { getCityStorage, setSenderStorage } from 'helper/storage';
import {
  getCreateOrderIDHashForFailedOrder,
  getInitialReduxOrder,
  getSenderToStorage,
} from 'helper/order';
import { isUsingPaxPersonalPayments } from 'helper/user';
import { paymentMethodsFromActionPayload } from 'helper/payment';
import {
  CLEAN_ALL,
  CREATE_ORDER,
  GET_QUOTES,
  GET_QUOTES_WITH_PROMO,
  GET_ADDRESS_SEARCH,
  GET_ADDRESS_TRANSLATION,
  GET_PAYMENT_METHODS,
  FETCH_SERVICE_TYPES,
  UPDATE_ORDER,
  UPDATE_ORDER_SHALLOW,
  UPDATE_BULK_ORDER,
  UPDATE_BULK_ORDER_SHALLOW,
  FETCH_SERVICE_TYPES_V2,
  CREATE_ORDER_V2,
  FETCH_ALL_PAYMENT_METHODS_SUCCESS,
  PROCESS_BULK_UPLOAD_DATA_COMPLETE_V2,
  PROCESS_BULK_UPLOAD_DATA_V2,
  PAX_FETCH_SERVICE_TYPES,
  PAX_FETCH_ALL_PAYMENT_METHODS_SUCCESS,
  SET_PAYMENT_METHODS,
} from 'actions/booking-actions';
import { transformServiceTypes } from 'selectors/booking-selectors';
import {
  LOCATION_DRAWER_ROUTE_OPTIMIZATION,
  UPDATE_OPTIMIZE_QUOTES_STATUS,
} from 'actions/app-actions';
import { QUOTE_OPTIMIZED_FAILED } from '../constants/booking-constants';

const BookingReducer = (state = {}, action = {}) => {
  switch (action.type) {
    case CLEAN_ALL: {
      return Object.assign({}, state, {
        addressSearch: {},
        addressTranslate: {},
        createdOrders: [],
        paymentMethods: state.paymentMethods,
        promoQuotes: {},
        quotes: {},
        serviceTypes: state.serviceTypes,
      });
    }

    case CREATE_ORDER.SUCCESS:
      return { ...state, createdOrders: action.data.deliveries, quotes: {} };

    case CREATE_ORDER.ERROR: {
      return { ...state, createdOrders: [], quotes: {} };
    }

    case GET_QUOTES.SUCCESS:
      return { ...state, quotes: action.data };

    case GET_QUOTES_WITH_PROMO.START:
      return { ...state, promoQuotes: {} };
    case GET_QUOTES_WITH_PROMO.SUCCESS:
      return { ...state, promoQuotes: action.data };

    case GET_ADDRESS_SEARCH.START:
      return { ...state, addressTranslate: {} };
    case GET_ADDRESS_SEARCH.SUCCESS:
      return { ...state, addressSearch: action.data.resp };

    case GET_ADDRESS_TRANSLATION.START:
      return { ...state, addressSearch: {}, addressTranslate: {} };
    case GET_ADDRESS_TRANSLATION.SUCCESS: {
      return { ...state, addressTranslate: action.data.resp };
    }

    case GET_PAYMENT_METHODS.SUCCESS:
      return { ...state, paymentMethods: action.data };

    case FETCH_SERVICE_TYPES.START:
      if (state.serviceTypes) {
        return { ...state };
      }
      return { ...state, serviceTypes: {} };
    case FETCH_SERVICE_TYPES.SUCCESS: {
      const { response } = action.data;
      if (!response.servicesConfig) {
        response.servicesConfig = {};
      }
      return {
        ...state,
        serviceTypes: response,
      };
    }

    // ----------------------V2------------------------
    case UPDATE_ORDER_SHALLOW:
    case UPDATE_ORDER: {
      return {
        ...state,
        order: action.payload,
      };
    }

    case LOCATION_DRAWER_ROUTE_OPTIMIZATION.ERROR: {
      return {
        ...state,
        optimizeQuotesStatus: {
          status: QUOTE_OPTIMIZED_FAILED,
        },
      };
    }
    case LOCATION_DRAWER_ROUTE_OPTIMIZATION.SUCCESS: {
      const { quoteV2, optimizeQuotesStatus } = action.payload;
      return {
        ...state,
        optimizeQuotesStatus,
        quoteV2,
      };
    }
    case UPDATE_OPTIMIZE_QUOTES_STATUS: {
      return {
        ...state,
        optimizeQuotesStatus: {
          ...state.optimizeQuotesStatus,
          ...action.payload,
        },
      };
    }

    case UPDATE_BULK_ORDER_SHALLOW:
    case UPDATE_BULK_ORDER: {
      const { index, order } = action.payload;

      // single order update in bulk screen
      if (!isNaN(index)) {
        let newOrders = [];
        if (state.orders.length <= index) {
          // when adding new order
          newOrders = [...state.orders, order];
        } else {
          newOrders = [...state.orders];
          newOrders.splice(index, 1, order);
        }
        return {
          ...state,
          orders: newOrders,
        };
      }

      // Bulk upload review OR remove order in bulk order page
      const { orders } = action.payload;
      return {
        ...state,
        orders,
      };
    }

    case PAX_FETCH_SERVICE_TYPES.SUCCESS: {
      const paxCityID = _get(action, 'body.input.queryParams.cityID');
      const { response: paxResponse } = action.data;
      const paxTransformedServiceTypes = transformServiceTypes(
        paxResponse,
        state.paymentMethods,
      );
      return {
        ...state,
        serviceTypeMap: Object.assign({}, state.serviceTypeMap, {
          [paxCityID]: paxTransformedServiceTypes,
        }),
      };
    }

    case FETCH_SERVICE_TYPES_V2.SUCCESS: {
      const { response } = action.data;
      const cityID = _get(action, 'body.request.queryParams.cityID');
      const transformedServiceTypes = transformServiceTypes(
        response,
        state.paymentMethods,
      );
      return {
        ...state,
        serviceTypeMap: Object.assign({}, state.serviceTypeMap, {
          [cityID]: transformedServiceTypes,
        }),
      };
    }

    case CREATE_ORDER_V2.SUCCESS: {
      const userInfo = _get(action, 'reduxState.app.userInfo');
      const selectedCityID = getCityStorage().cityID || null;
      const defaultServiceTypes = selectedCityID
        ? state.serviceTypeMap[selectedCityID]
        : state.serviceTypeMap[Object.keys(state.serviceTypeMap)[0]];

      const failedOrders = _get(action, 'data.deliveries', []).reduce(
        (acc, order) => (!_isEmpty(order.errors) ? acc.concat(order) : acc),
        [],
      );

      const initialOrder = getInitialReduxOrder(defaultServiceTypes);
      let singleInitialOrder = initialOrder;
      const isSingleBook = _get(action, 'data.deliveries', []).length === 1;
      if (isSingleBook && _isEmpty(failedOrders)) {
        const safeID = _get(userInfo, 'user.safeID');
        const senderInfo = getSenderToStorage(state.order);
        singleInitialOrder = {
          ...initialOrder,
          ...senderInfo,
        };
        setSenderStorage(selectedCityID, safeID, senderInfo);
      }

      if (_isEmpty(failedOrders)) {
        return {
          ...state,
          order: singleInitialOrder,
          orders: [initialOrder],
        };
      }

      const reduxOrders = _keyBy([].concat(state.order, state.orders), order =>
        getCreateOrderIDHashForFailedOrder(userInfo, order),
      );
      const reduxFailedOrders = failedOrders.map(
        failedOrder => reduxOrders[failedOrder.createRequestID],
      );

      return {
        ...state,
        order:
          reduxFailedOrders.length === 1
            ? reduxFailedOrders[0]
            : singleInitialOrder,
        orders:
          reduxFailedOrders.length > 1 ? reduxFailedOrders : [initialOrder],
      };
    }

    case FETCH_ALL_PAYMENT_METHODS_SUCCESS:
    case SET_PAYMENT_METHODS:
      return {
        ...state,
        paymentMethods: paymentMethodsFromActionPayload(action.payload),
      };

    case PAX_FETCH_ALL_PAYMENT_METHODS_SUCCESS:
      return isUsingPaxPersonalPayments()
        ? {
            ...state,
            paymentMethods: paymentMethodsFromActionPayload(action.payload),
          }
        : { ...state };

    case PROCESS_BULK_UPLOAD_DATA_V2:
      return {
        ...state,
        bulkUploadProcessing: { uploadingExcel: true, success: false },
      };

    case PROCESS_BULK_UPLOAD_DATA_COMPLETE_V2:
      return { ...state, bulkUploadProcessing: action.payload };

    default:
      return state;
  }
};

export default BookingReducer;
